import cx from 'classnames';
import {type CSSProperties, memo} from 'react';
import {DeliveryStatus, type Guest, isHostGuest} from '~/ViewInvitation/types/guests';
import {Button} from '~library/atoms/Button';
import {CheckmarkIcon} from '~library/svg/v2/CheckmarkIcon';
import {UserIcon} from '~library/svg/v2/UserIcon';
import {XIcon} from '~library/svg/v2/XIcon';
import {getColors} from '~library/utils/styles';

import * as styles from './Avatar.module.scss';

const colors = getColors();

const questionMarkIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="6"
    height="8"
    viewBox="0 0 6 8"
    fill="none"
    role="img"
    aria-label="Maybe"
  >
    <path
      d="M3 0C1.6215 0 0.5 1.1215 0.5 2.5H1.5C1.5 1.673 2.173 1 3 1C3.827 1 4.5 1.673 4.5 2.5C4.5 3.0345 4.273 3.2325 3.7595 3.6275C3.5685 3.7745 3.353 3.9405 3.1465 4.1465C2.4905 4.802 2.4975 5.4485 2.5 5.5V6.5H3.5V5.4955C3.5 5.4835 3.5115 5.195 3.8535 4.8535C4.0135 4.6935 4.1945 4.5545 4.369 4.42C4.899 4.012 5.5 3.55 5.5 2.5C5.5 1.1215 4.3785 0 3 0ZM2.5 7H3.5V8H2.5V7Z"
      fill={colors.charcoal as string}
    />
  </svg>
);

const exclamationMarkIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    role="img"
    aria-label="Delivery error"
  >
    <path
      d="M5.25 9H6.75V10.5H5.25V9ZM5.25 1.5H6.75V7.875H5.25V1.5Z"
      fill={colors.charcoal as string}
    />
  </svg>
);

const getCharacters = (name: string) =>
  name
    .trim()
    .split(' ')
    .slice(0, 2)
    .map((char) => char.charAt(0).toUpperCase())
    .join('');

const getColor = (guest: Guest) => {
  if (isHostGuest(guest) && guest.deliveryStatus === DeliveryStatus.FAILED) {
    return colors.feedbackError;
  }
  if (guest.rsvpResponse === 'yes') {
    return colors.rsvpYes;
  }
  if (guest.rsvpResponse === 'no') {
    return colors.rsvpNo;
  }
  if (guest.rsvpResponse === 'maybe') {
    return colors.rsvpMaybe;
  }
};

const getIcon = (guest: Guest) => {
  if (isHostGuest(guest) && guest.deliveryStatus === DeliveryStatus.FAILED) {
    return exclamationMarkIcon;
  }
  if (guest.rsvpResponse === 'yes') {
    return <CheckmarkIcon ratio={0.375} />;
  }
  if (guest.rsvpResponse === 'no') {
    return <XIcon ratio={0.375} />;
  }
  if (guest.rsvpResponse === 'maybe') {
    return questionMarkIcon;
  }
};

export const Badge = memo(({guest}: {guest: Guest}) => {
  const color = getColor(guest);
  const icon = getIcon(guest);
  if (!color || !icon) return null;

  return (
    <span className={styles.badge} style={{backgroundColor: color}}>
      {icon}
    </span>
  );
});

Badge.displayName = 'Badge';

interface AvatarPreviewProps {
  size?: number;
  showShadow?: boolean;
}

export const AvatarPreview = memo(({size = 40, showShadow}: AvatarPreviewProps) => {
  const style: CSSProperties = {width: `${size}px`, height: `${size}px`};
  return (
    <div className={styles.button}>
      <span
        className={cx(styles.initials, {
          [styles.dropShadow]: showShadow,
        })}
        style={style}
      >
        <UserIcon ratio={0.75} />
      </span>
    </div>
  );
});

AvatarPreview.displayName = 'AvatarPreview';

interface AvatarImgProps {
  guest: Guest;
  size?: number;
  showShadow?: boolean;
}

export const AvatarImg = memo(({guest, size = 40, showShadow}: AvatarImgProps) => {
  const fontSize = size > 30 ? 15 : 10;
  const style: CSSProperties = size ? {width: `${size}px`, height: `${size}px`} : {};
  style.fontSize = `${fontSize}px`;

  if (!guest) return null;

  return guest.avatarUrl ? (
    <img
      className={cx(styles.avatarImg, {
        [styles.dropShadow]: showShadow,
      })}
      src={guest.avatarUrl}
      arial-label={guest.name}
      alt={`${guest.name} avatar`}
      width={size}
      height={size}
    />
  ) : (
    <span
      className={cx(styles.initials, {
        [styles.dropShadow]: showShadow,
      })}
      style={style}
    >
      {getCharacters(guest.name)}
    </span>
  );
});

AvatarImg.displayName = 'AvatarImg';

interface AvatarProps {
  guest: Guest;
  onClick?: () => void;
  size?: number;
  hideBadge?: boolean;
  showShadow?: boolean;
}

export const Avatar = memo(({guest, onClick, size = 40, hideBadge, showShadow}: AvatarProps) => {
  return (
    <div
      className={cx(styles.avatarContainer, {
        [styles.hasDropShadow]: showShadow,
      })}
    >
      {onClick ? (
        <Button
          variant="transparent"
          data-qa-id={`avatar-for-${guest.userId}`}
          onClick={onClick}
          className={styles.button}
          title={guest.name}
        >
          <AvatarImg guest={guest} size={size} showShadow={showShadow} />
        </Button>
      ) : (
        <div className={styles.button}>
          <AvatarImg guest={guest} size={size} showShadow={showShadow} />
        </div>
      )}
      {hideBadge || <Badge guest={guest} />}
    </div>
  );
});

Avatar.displayName = 'Avatar';
